<template lang="pug">
section.section(
    :class="classes"
    @click="editSection"
)
    include base.pug
    .content-wrappers(v-if="!isEmpty || $slots.default")
        slot
        .preview(v-if="!isEmpty && categoryOptions.length")
            //- wrap el-tag to bypass transition
            .content-wrapper.own-content
                .categories
                    el-tag(
                        v-for="category in getCategories(section)"
                        type="info"
                        :key="category.key"
                        size="large"
                        effect="dark"
                    ) {{ category.name }}
                .tags(v-if="tags.length")
                    el-tag(
                        v-for="tag in getTags(section)"
                        type="info"
                        :key="tag.key"
                        size="small"
                        effect="dark"
                    ) {{ tag.name }}

            template(v-if="referenceFeatureEnabled && getReferences(section).length")
                //- transition-group(name="fade" type="transition" tag="div" class="referenced-contents")
                .reference(v-for="reference in getReferences(section)" :key="reference.cms.id")
                    h4 {{ reference.cms.name }}
                    .categories
                        el-tag(
                            v-for="category in reference.categories"
                            :key="category.key"
                            type="info"
                            effect="dark"
                            size="small"
                        ) {{ category.name }}
                    .tags
                        el-tag(
                            v-for="tag in reference.tags"
                            :key="tag.key"
                            type="info"
                            effect="dark"
                            size="mini"
                        ) {{ tag.name }}

        .error(v-if="!categoryOptions.length")
            el-alert(type="warning" effect="dark" :title="$t('There are no categories for this template!')")

    el-drawer.full-height-drawer.visible-overflow(
        v-if="editable"
        :title="$t('Edit {section}', { section: section.name })"
        :visible.sync="scopedUI.form"
        :close-on-press-escape="true"
        :wrapper-closable="false"
        :append-to-body="true"
        size="920px"
        :modal="true"
        @closed="resetDrawer"
    )
        .drawer-body(v-loading="drawer.loading")
            el-tabs(
                v-if="!drawer.loading"
                v-model="drawer.tab"
                tab-position="top"
            )
                el-tab-pane(name="content" :label="$t('Content')")
                    .tab-content
                        .content-wrapper
                            el-form-item(:label="$t('Categories')")
                                el-select.is-full-width.dark(
                                    placeholder=""
                                    v-model="categories"
                                    value-key="key"
                                    effect="dark"
                                    multiple
                                )
                                    el-option(
                                        v-for="category in categoryOptions"
                                        :key="category.key" :label="category.name" :value="category"
                                        :disabled="category.isDisabled"
                                    )
                            el-form-item(v-if="tagOptions.length" :label="$t('Tags')" size="small")
                                el-select.is-full-width.dark(
                                    placeholder=""
                                    v-model="tags"
                                    value-key="id"
                                    size="small"
                                    effect="dark"
                                    multiple
                                )
                                    el-option(
                                        v-for="tag in tagOptions"
                                        :key="tag.key" :label="tag.name" :value="tag"
                                    )

                            template(v-if="referenceFeatureEnabled && references.length")
                                //- transition-group(name="fade" type="transition" tag="div" class="referenced-contents")
                                component.reference(
                                    v-for="reference in references"
                                    :key="reference.cms.id"
                                    :is="reference.editable ? 'a' : 'div'"
                                    v-bind="reference.editable ? { href: '#'} : null"
                                    @click.stop.prevent="openModal(reference)"
                                )
                                    h4 {{ reference.cms.name }}
                                    .categories
                                        el-tag(
                                            v-for="category in reference.categories"
                                            :key="category.key"
                                            type="info"
                                            effect="dark"
                                            size="small"
                                        ) {{ category.name }}
                                    .tags
                                        el-tag(
                                            v-for="tag in reference.tags"
                                            :key="tag.key"
                                            type="info"
                                            effect="dark"
                                            size="mini"
                                        ) {{ tag.name }}

                                    el-button.delete-reference(
                                        v-if="reference.editable"
                                        circle type="danger" size="mini"
                                        icon="el-icon-fa-trash-alt far"
                                        @click.prevent.stop="deleteReference(reference)"
                                    )

                            el-button.button-referenced-content(
                                v-if="referenceFeatureEnabled && cmsOptions && cmsOptions.filter(el => ! el.isDisabled).length"
                                type="primary"
                                icon="el-icon-fa-plus fas"
                                size="mini"
                                @click="event => openModal()"
                            ) {{ $t('Add referenced content') }}
                el-tab-pane(name="settings" :label="$t('Settings')")
                    .block-settings
                        .settings-wrap
                            include settings.pug
        .drawer-footer
            include section-drawer-footer.pug

    el-dialog(
        :title="modalTitle"
        :visible.sync="modalVisibility"
        :close-on-click-modal="false"
        :append-to-body="true"
        top="10vh"
        width="720px"
        @closed="resetModal"
    )
        el-form.referenced-categoryposts-modal-form(
            :model="modal"
            label-position="top"
            :rules="rules"
            ref="modal"
            v-loading="modalLoading"
            @submit.prevent.native="submitModalForm"
        )
            el-form-item(:label="$t('CMS')" prop="cms")
                el-select.is-full-width(
                    placeholder=""
                    v-model="modal.cms"
                    :disabled="cmsOptions.length <= 1 || modalType !== 'new'"
                )
                    el-option(
                        v-for="option in cmsOptions"
                        :key="option.id"
                        :label="option.name"
                        :value="option.id"
                        :disabled="option.isDisabled"
                    )

            .loader-overlay(
                v-loading="modalOptions.tag.loading"
            )
                el-form-item(:label="$t('Categories')" prop="categories")
                    el-select.is-full-width.dark(
                        placeholder=""
                        v-model="referencedCategories"
                        value-key="key"
                        effect="dark"
                        multiple
                    )
                        el-option(
                            v-for="category in referenceCategoryOptions"
                            :key="category.key" :label="category.name" :value="category"
                            :disabled="category.isDisabled"
                        )
                transition(name="slide-vertical")
                    el-form-item(
                        v-if="referenceTagOptions.length"
                        :label="$t('Tags')"
                        size="small"
                        prop="tags"
                    )
                        el-select.is-full-width.dark(
                            placeholder=""
                            v-model="modal.tags"
                            value-key="id"
                            size="small"
                            effect="dark"
                            multiple
                        )
                            el-option(
                                v-for="tag in referenceTagOptions"
                                :key="tag.key" :label="tag.name" :value="tag"
                            )

        template(booking="footer")
            .modal-footer
                .left
                .right
                    el-button(
                        size="mini"
                        icon="el-icon-fa-times fas"
                        @click="closeModal"
                    ) {{ $t('Dismiss') }}
                    el-button(
                        :disabled="modalLoading"
                        size="mini"
                        type="primary"
                        icon="el-icon-fa-check fas"
                        @click="submitModalForm"
                    ) {{ $t('Ok') }}

</template>

<i18n>
{
    "en": {
        "Edit {section}": "Edit {section}"
    },
    "de": {
        "Edit {section}": "{section} bearbeiten",
        "Content": "Inhalt",
        "Settings": "Einstellungen",
        "Category": "Kategorie",
        "Categories": "Kategorien",
        "Referenced content": "Fremdinhalt",
        "Add referenced content": "Fremdinhalt hinzufügen",
        "New referenced content": "Neuer Fremdinhalt",
        "Edit referenced content": "Fremdinhalt bearbeiten",
        "Tags": "Tags",
        "There are no categories for this template!": "Für dieses Template gibt es keine Kategorien!",
        "from": "ab",
        "to": "bis",
        "Margin top": "Abstand oben",
        "Margin bottom": "Abstand unten",
        "none": "Ohne",
        "small": "Klein",
        "medium": "Mittel",
        "large": "Groß",
        "default": "Standard",
        "custom": "Benutzerdefiniert",
        "Everybody": "Jedem",
        "Members": "Mitgliedern",
        "Guests": "Gästen",
        "Any": "Alle",
        "Tags": "Tags",
        "Select tags": "Tags auswählen",
        "Accessible by": "Zugänglich von",
        "e.g. 40px, 10%, 2em, ...": "z.B. 40px, 10%, 2em, ...",
        "desktop": "Desktop",
        "tablet": "Tablet",
        "mobile": "Telefon",
        "Visible on":"Anzeigen auf",
        "Element is displayed on":"Inhaltselement <br> wird dargestellt auf"
    }
}
</i18n>

<script>
import _ from 'lodash';

import store from '@/store';
import Section from './Section';

import TagApi from '@/store/api/cms/tag';

import { useSetupStore } from '@/stores';

export default {
    name: 'CategoryPosts',
    extends: Section,
    data: () => ({
        modalVisibility: false,
        modalLoading: false,
        modalType: 'new', // valid options: 'new' / fresh / 'existing'
        modal: {
            cms: undefined,
            categories: [],
            tags: []
        },
        modalOptions: {
            tag: {
                loading: false,
                items: []
            }
        }
    }),
    watch: {
        'modal.cms'() {
            this.fetchReferenceTagOptions();

            if (this.modalType === 'new') {
                this.modal.tags = [];

                // NOTE: as long as the referenced cms must have the same template
                // and we have no dynamic categories
                // it is not necessary to unset the selected categories

                // this.modal.categories = []
            }
        }
    },
    computed: {
        editable() {
            return true;
        },
        referenceFeatureEnabled() {
            let features = _.get(this.activeCms, 'config.template.features') || [];
            return !!features.find(el => el === 'referenced-categoryposts');
        },
        cmsOptions() {
            const cmss = _.cloneDeep(this.cmss);
            return cmss.filter(el => {
                const existsInReferences = !!this.references.find(reference => reference.cms.id === el.id);
                if (existsInReferences) el.isDisabled = true;
                else delete el.isDisabled;

                return _.get(el, 'config.template.id') === this.activeCms.config.template.id
                    && el.id !== this.activeCms.id;
                // && existsInReferences === false
            });
        },
        allowedCategories() {
            let categories = [];

            const sectionConfigCategories = _.get(this.sectionConfig, 'categories')
                || _.get(this.defaultConfig, 'categories') || [];

            let options = _.get(this.activeCms, 'config.template.categories') || [];

            if (sectionConfigCategories.length) {
                const keys = options.map(el => el.key);
                categories = sectionConfigCategories.filter(el => keys.includes(el.key));
            } else {
                categories = options;
            }

            return categories;
        },
        isEmpty() {
            // let empty = !this.$slots.default
            let empty = true

            if (empty && this.section.categories && this.section.categories.length) empty = false
            if (empty && this.section.tags && this.section.tags.length) empty = false

            // if (empty && this.section.informations && this.section.informations.length) empty = false
            // if (empty && this.section.eventCategories && this.section.eventCategories.length) empty = false
            // if (empty && this.section.shopCategories && this.section.shopCategories.length) empty = false
            // if (empty && this.section.products && this.section.products.length) empty = false

            // const contents = _.get(this.section, 'contents') || []
            // contents.forEach(content => {
            //     if (! empty) return
            //     if (content.addresses && content.addresses.length) empty = false
            //     if (content.images && content.images.length) empty = false
            //     if (content.media && content.media.length) empty = false
            //     if (content.title || content.sub_title || content.text) empty = false
            // })

            return empty
        },
        categories: {
            get() {
                const section = this.drawer.section || this.section
                return this.getCategories(section)
            },
            set(selection) {
                let categories = [];

                const section = this.drawer.section || this.section

                if (selection && selection.length) {
                    categories = selection.map(el => {
                        let category = {
                            key: el.key,
                            name: el.name,
                            cms: {
                                id: this.activeCms.id
                            }
                        };

                        if (el.id) {
                            category.id = el.id;
                        }

                        return category;
                    });
                }

                if (section.categories && section.categories.length) {
                    const referencedCategories = section.categories.filter(el => {
                        if (el && el.cms && el.cms.id) return el.cms.id !== this.activeCms.id;
                    });
                    if (referencedCategories.length) categories = [...categories, ...referencedCategories];
                }

                // const payload = {
                //     i: this.i,
                //     // p: this.p,
                //     s: this.s,
                //     categories
                // };
                // store.commit(`${this.module}/form/sectionCategories`, payload);

                this.$set(section, 'categories', categories);
            }
        },
        tags: {
            get() {
                const section = this.drawer.section || this.section
                return this.getTags(section)
            },
            set(selection) {
                const section = this.drawer.section || this.section

                let tags = [];
                if (selection && selection.length) {
                    tags = selection.map(el => {
                        let tag = {
                            key: el.key,
                            name: el.name,
                            cms: {
                                id: this.activeCms.id
                            }
                        };

                        if (el.id) {
                            tag.id = el.id;
                        }

                        return tag;
                    });
                }

                if (section.tags && section.tags.length) {
                    const referencedTags = section.tags.filter(el => el.cms.id !== this.activeCms.id);
                    if (referencedTags.length) tags = [...tags, ...referencedTags];
                }

                // const payload = {
                //     i: this.i,
                //     // p: this.p,
                //     s: this.s,
                //     tags
                // };
                // store.commit(`${this.module}/form/sectionTags`, payload);

                this.$set(section, 'tags', tags);
            }
        },
        tagOptions() {
            // const options = store.getters[`${this.module}/options`];
            return _.get(this.options, 'tag.items') || [];
        },
        categoryOptions() {
            let options = _.get(this.activeCms, 'config.template.categories') || [];
            options = _.cloneDeep(options);

            options.forEach(option => {
                if (this.allowedCategories.find(el => el.key === option.key)) option.isDisabled = false;
                else option.isDisabled = true;
            });

            return options;
        },

        references() {
            // let references = []
            // this.cmss.filter(el => el.id !== this.activeCms.id).forEach(cms => {
            //     cms = {
            //         id: cms.id,
            //         name: cms.name,
            //     }

            //     const categories = this.section.categories.filter(el => el.cms.id === cms.id)
            //     const tags = this.section.tags.filter(el => el.cms.id === cms.id)

            //     if (categories.length || tags.length) {
            //         references.push({ cms, categories, tags })
            //     }
            // })
            // return references

            return this.getReferences(this.drawer.section || this.section)
        },
        referenceTagOptions() {
            return this.modalOptions.tag.items || [];
        },
        referenceCategoryOptions() {
            // NOTE: as long as the referenced cms must have the same template
            // and we have no dynamic categories
            // it is not necessary to get the foreign categories

            // let options = _.get(this.modal.cms, 'config.template.categories')
            // options = _.cloneDeep(options)

            // options.forEach(option => {
            //     if (this.allowedCategories.find(el => el.key === option.key)) option.isDisabled = false
            //     else option.isDisabled = true
            // })

            // return options

            return this.categoryOptions;
        },
        referencedCategories: {
            get() {
                return this.modal.categories;
            },
            set(categories) {
                categories = categories.map(el => {
                    return {
                        key: el.key,
                        name: el.name
                    };
                });
                this.modal.categories = categories;
            }
        },

        modalTitle() {
            // let title = this.$t('New referenced content')
            // if (this.modalType === 'existing') {
            //     title = this.$t('Edit referenced content')
            // }
            // return title

            return this.$t('Referenced content');
        },
        rules() {
            return {
                cms: 'required',
                categories: 'required'
            };
        },

        setupStore() {
            return useSetupStore()
        },
        cmss() {
            return this.setupStore.cmss
        },
    },
    methods: {
        getCategories(section) {
            // return _.get(this.form.menuItems[this.i].page.sections[this.s], 'categories')
            const categories = _.get(section, 'categories') || [];
            // if (! this.allowedCategories || ! this.allowedCategories.length) return categories;

            // filter all selected options by possible ones from config
            const keys = this.allowedCategories.map(el => el.key);
            return categories.filter(el => {
                return keys.includes(el.key) && (el.cms === undefined || el.cms.id === this.activeCms.id);
            });
        },
        getTags(section) {
            let tags = _.get(section, 'tags') || [];
            return tags.filter(el => el.cms.id === this.activeCms.id);
        },
        getReferences(section) {
            let references = {};

            const categories = _.get(section, 'categories') || []
            categories.forEach(category => {
                if (category.cms === undefined) return;

                const key = category.cms.id;
                if (key === this.activeCms.id) return;

                if (references[key] === undefined) {
                    references[key] = {
                        cms: category.cms,
                        categories: [],
                        tags: [],
                        editable: !!this.cmss.find(el => el.id === key)
                    };
                }

                references[key].categories.push(category);
            });

            const tags = _.get(section, 'tags') || []
            tags.forEach(tag => {
                const key = tag.cms.id;
                if (key === this.activeCms.id) return;

                if (references[key] === undefined) {
                    references[key] = {
                        cms: tag.cms,
                        categories: [],
                        tags: [],
                        editable: !!this.cmss.find(el => el.id === key)
                    };
                }

                references[key].tags.push(tag);
            });

            return Object.values(references);
        },
        openModal(reference = null) {
            if (!reference) {
                this.modalLoading = true;
                this.modalVisibility = true;
                this.modal.cms = this.cmsOptions.find(el => !el.isDisabled).id;

                // NOTE: as long as the referenced cms must have the same template
                // and we have no dynamic categories
                // it is allowed to select the parent categories by default
                this.referencedCategories = this.categories;
                this.modalType = 'new';
            } else if (reference.editable) {
                this.modalLoading = true;
                this.modalVisibility = true;

                this.modalType = 'existing';

                this.modal = _.cloneDeep(reference);
                this.modal.cms = reference.cms.id;
            }

            this.modalLoading = false;
        },
        closeModal() {
            this.modalVisibility = false;
            this.modalLoading = false;
        },
        submitModalForm() {
            this.$refs.modal.validate(valid => {
                if (valid) {
                    const section = this.drawer.section || this.section

                    const cms = this.cmss.find(el => el.id === this.modal.cms);

                    // handle categories

                    let categories = [];
                    if (this.modal.categories && this.modal.categories.length) {
                        categories = this.modal.categories.map(el => {
                            let category = {
                                key: el.key,
                                name: el.name,
                                cms: {
                                    id: this.modal.cms,
                                    name: cms.name
                                }
                            };

                            if (el.id) {
                                category.id = el.id;
                            }

                            return category;
                        });
                    }

                    if (section.categories && section.categories.length) {
                        const otherCategories = section.categories.filter(el => el.cms.id !== this.modal.cms);
                        if (otherCategories.length) categories = [...categories, ...otherCategories];
                    }

                    // store.commit(`${this.module}/form/sectionCategories`, {
                    //     i: this.i,
                    //     // p: this.p,
                    //     s: this.s,
                    //     categories
                    // });

                    this.$set(section, 'categories', categories);

                    // handle tags

                    let tags = [];
                    if (this.modal.tags && this.modal.tags.length) {
                        tags = this.modal.tags.map(el => {
                            let tag = {
                                key: el.key,
                                name: el.name,
                                cms: {
                                    id: this.modal.cms,
                                    name: cms.name
                                }
                            };

                            if (el.id) {
                                tag.id = el.id;
                            }

                            return tag;
                        });
                    }

                    if (section.tags && section.tags.length) {
                        const otherTags = section.tags.filter(el => el.cms.id !== this.modal.cms);
                        if (otherTags.length) tags = [...tags, ...otherTags];
                    }

                    // store.commit(`${this.module}/form/sectionTags`, {
                    //     i: this.i,
                    //     // p: this.p,
                    //     s: this.s,
                    //     tags
                    // });

                    this.$set(section, 'tags', tags);

                    this.closeModal();
                }
            });
        },
        resetModal() {
            this.modal = {
                cms: undefined,
                categories: [],
                tags: []
            };
            this.$refs.modal.resetFields();
        },
        fetchReferenceTagOptions() {
            if (this.modal.cms) {
                this.modalOptions.tag.loading = true;
                return TagApi.fetchList(this.modal.cms)
                    .then(tags => {
                        this.modalOptions.tag.items = tags;
                    })
                    .finally(() => {
                        this.modalOptions.tag.loading = false;
                    });
            }
        },
        deleteReference(reference) {
            const cms = reference.cms.id;
            const section = this.drawer.section || this.section

            // update categories

            let categories = [];
            if (section.categories && section.categories.length) {
                categories = section.categories.filter(el => el.cms.id !== cms);
            }

            // store.commit(`${this.module}/form/sectionCategories`, {
            //     i: this.i,
            //     // p: this.p,
            //     s: this.s,
            //     categories
            // });

            this.$set(section, 'categories', categories);

            // update tags

            let tags = [];
            if (section.tags && section.tags.length) {
                tags = section.tags.filter(el => el.cms.id !== cms);
            }

            // store.commit(`${this.module}/form/sectionTags`, {
            //     i: this.i,
            //     // p: this.p,
            //     s: this.s,
            //     tags
            // });

            this.$set(section, 'tags', tags);
        }
    }
};
</script>

<style lang="sass" scoped>
.viewports
    margin-right: 12px
    .el-tooltip
        margin: 0 3px
        color: #9ca3af
        .inner
            text-align: center
.preview
    .content-wrapper
        // margin-bottom: 8px
        & >
            .categories,
            .tags
                display: flex
                flex-wrap: wrap
                gap: 5px
                .el-tag + .el-tag
                    margin-left: 0

.error,
.referenced-contents,
.button-referenced-content
    margin-bottom: 10px

.tags
    margin-top: 4px
    .el-tag
        font-size: 0.7rem

.reference
    position: relative
    display: block
    text-decoration: none
    // color: white
    // background-image: repeating-linear-gradient(-45deg, transparent, transparent 2px, rgba(255,255,255,.4) 2px, rgba(255,255,255,.4) 21px)
    // background-size: 30px 60px
    background-color: rgba(0,0,0,.05)
    margin-left: -20px
    padding-left: 20px
    margin-right: -20px
    padding-right: 20px
    // margin-top: -10px
    padding-top: 10px
    padding-bottom: 10px
    margin-bottom: 10px
    transition: background-color .15s ease
    & + .reference
        margin-top: 10px
    h4
        line-height: 1.3
        margin-top: 0 !important
        margin-bottom: 0.5rem !important
    .el-tag + .el-tag
        margin-left: 4px
a.reference
    &:hover,
    &:focus
        background-color: rgba($--color-primary,.1)
    .delete-reference
        position: absolute
        right: 12px
        top: 12px

.preview .reference
    margin-top: 1rem
    margin-left: -12px
    padding-left: 12px
    margin-right: -12px
    padding-right: 12px
    &:last-child
        margin-bottom: -12px

.modal-footer
    display: flex
    justify-content: space-between
    .left
        display: flex
        align-items: center
</style>
